import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { wp } from "../api/WpApi";
import "../../styles/wpStyles.scss";
import { ItemListSchema } from "../seo/ItemListSchema";
import Breadcrumbs from "../helpers/Breadcrumbs";
import PageLoading from "./PageLoading";
import data from "./_setup.json";
import MetaTags from "../seo/MetaTags";
import { Helmet } from "react-helmet";
import DEFAULT_THUMBNAIL from "../../resources/not-found.png";
import Pagination from "./Pagination";

const CollectionsList = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [currentpage, setCurrentPage] = useState(1);
  const [totalpages, setTotalPages] = useState(null);
  const [perPage] = useState(50);

  const breadcrumbsItems = [
    { name: "Home", url: "/" },
    { name: "Collections", url: `/collections/` },
  ];

  const getCollections = useCallback(
    (page) => {
      setIsLoading(true);
      wp.categories()
        .perPage(perPage)
        .page(page)
        .then((cats) => {
          if (!Array.isArray(cats)) {
            setCollections([]);
            setTotalPages(1);
            setIsLoading(false);
            return;
          }

          // Filter categories with more than 3 posts
          const validCollections = cats.filter((cat) => cat.count > 3);

          const promises = validCollections.map((cat) =>
            wp
              .posts()
              .categories(cat.id)
              .perPage(1)
              .then((posts) => {
                const thumb =
                  posts && posts[0] && posts[0].yoast_head_json && posts[0].yoast_head_json.og_image
                    ? posts[0].yoast_head_json.og_image[0].url
                    : DEFAULT_THUMBNAIL;
                return { ...cat, thumbnail: thumb };
              })
              .catch(() => ({ ...cat, thumbnail: DEFAULT_THUMBNAIL }))
          );

          Promise.all(promises)
            .then((collsWithThumbs) => {
              setCollections(collsWithThumbs);
              // Approximate total pages based on all categories
              const totalPages = cats._paging && cats._paging.totalPages ? cats._paging.totalPages : 1;
              setTotalPages(totalPages);
              setIsLoading(false);
            })
            .catch((err) => {
              setError(err);
              setIsLoading(false);
            });
        })
        .catch((error) => {
          setError(error);
          setIsLoading(false);
        });
    },
    [perPage]
  );

  useEffect(() => {
    getCollections(currentpage);
  }, [currentpage, getCollections]);

  var itemListArray = [];
  var listCount = 1;

  if (error) {
    return (
      <div className="content">
        <Helmet>
          <meta name="robots" content="noindex"></meta>
        </Helmet>
        Error: {error.message}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container">
        <PageLoading />
      </div>
    );
  }

  if (Array.isArray(collections) && collections.length > 0) {
    return (
      <div className="content container">
        <br />
        <MetaTags name="description" content="Browse curated recipe collections organized by category" />
        <Helmet>
          <title>Recipe Collections | Andrew's Cookin'</title>
          <meta
            name="description"
            content="Explore our curated recipe collections. Each collection features a handpicked list of the best recipes in its category with detailed descriptions and photos."
          />
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_PUBLIC_URL}/collections/`}
          />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "Recipe Collections",
              description: "Curated recipe collections organized by category",
              url: `${process.env.REACT_APP_PUBLIC_URL}/collections/`,
              hasPart: collections.map((coll) => ({
                "@type": "Article",
                headline: `${coll.count} Best ${coll.name} Recipes`,
                url: `${process.env.REACT_APP_PUBLIC_URL}/collections/${coll.slug}/`,
                image: coll.thumbnail,
                description: coll.description || `Discover the best ${coll.name} recipes`,
              })),
            })}
          </script>
        </Helmet>

        <div className="row">
          <div className="grid grid-cols-1 md:grid-cols-3 justify-content-between align-items-center">
            <div className="md:col-start-2 md:col-end-2">
              <h1 className="fw-bold">Best Recipe Collections</h1>
              <small className="fw-light">
                Browse our curated collections of the best recipes Andrew's Cookin' has to offer.
              </small>
            </div>
          </div>
        </div>

        <Breadcrumbs items={breadcrumbsItems} />
        <hr />

        <div className="row">
          <div className="list-group col">
            {collections.map((collection) => {
              itemListArray.push({
                "@type": "ListItem",
                position: listCount++,
                url: `${process.env.REACT_APP_PUBLIC_URL}/collections/${collection.slug}/`,
              });

              return (
                <Link
                  key={collection.id}
                  to={`/collections/${collection.slug}/`}
                  className="shadow-sm list-group-item list-group-item-action transition ease-in-out delay-50 duration-300 grid grid-rows-3 md:grid-cols-2"
                >
                  <h2
                    className="row-span-1"
                    style={{ padding: 20 }}
                    dangerouslySetInnerHTML={{ __html: "Top "+collection.count+" Best "+collection.name+" Recipes" }}
                  />
                  <div className="row-span-1 md:grid md:grid-cols-6 gap-6 md:justify-items-between justify-items-center">
                    <div className="post-thumbnail md:order-1 md:col-span-1">
                      <img
                        loading="lazy"
                        src={collection.thumbnail || DEFAULT_THUMBNAIL}
                        alt={collection.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_THUMBNAIL;
                        }}
                      />
                    </div>
                    <p
                      className="md:order-2 md:col-span-5 md:p-4 text-center md:px-8"
                      dangerouslySetInnerHTML={{ __html: collection.description }}
                    />
                  </div>
                </Link>
              );
            })}

            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(ItemListSchema(itemListArray))}
              </script>
            </Helmet>
          </div>
        </div>

        {currentpage <= totalpages && (
          <div className="row">
            <div className="col-md">
              <div className="row">
                <div className="col-md d-flex justify-content-center align-items-center my-5">
                  <Pagination
                    currentPage={currentpage}
                    totalPages={totalpages}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <hr />
      </div>
    );
  }

  return <div className="content">No collections available.</div>;
};

export default CollectionsList;
